import type { Slice } from "@reduxjs/toolkit"
import { combineSlices, configureStore } from "@reduxjs/toolkit"
import {
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useDispatch as useDispatchBase,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useSelector as useSelectorBase,
} from "react-redux"
import { setupListeners } from "@reduxjs/toolkit/query/react"
import { rememberEnhancer, rememberReducer } from "redux-remember"
import baseApi from "./baseApi"
import prefsSlice from "./prefsSlice"
import authSlice from "@/auth/slice"

export const rootReducer = combineSlices(baseApi, authSlice, prefsSlice)

// Not checking immutability or serializability for anything by RTK Query
const ignoredPaths = ["api"]

const store = configureStore({
  reducer: rememberReducer(rootReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: { ignoredPaths },
      serializableCheck: { ignoredPaths },
    }).concat(baseApi.middleware),
  enhancers: (getDefaultEnhancers) => {
    const enhancers = getDefaultEnhancers()
    if (typeof window !== "undefined") {
      return enhancers.concat(rememberEnhancer(window.localStorage, ["prefs"]))
    }

    return enhancers
  },
})

setupListeners(store.dispatch)

type AppDispatch = typeof store.dispatch
export const useDispatch = useDispatchBase.withTypes<AppDispatch>()

export type RootState = ReturnType<typeof rootReducer>
export const useSelector = useSelectorBase.withTypes<RootState>()

export const injectSlice = <TSlice extends Slice>(slice: TSlice) => {
  rootReducer.inject(slice)

  // dummy dispatch to init slice
  store.dispatch({ type: `@@SLICE/${slice.name}` })

  return useSelectorBase.withTypes<
    RootState & { [TPath in TSlice["name"]]: ReturnType<TSlice["reducer"]> }
  >()
}

export default store
